import React, { useState, useEffect, useCallback } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  TextField,
  Grid,
  Paper,
  IconButton,
  InputAdornment,
  Typography,
  Tooltip,
  Link,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import {
  Visibility,
  VisibilityOff,
  HelpOutline,
  FileCopy,
} from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { Link as RouterLink } from "react-router-dom/cjs/react-router-dom.min";

const GCAL_OAUTH_REDIRECT_SUFFIX = "/google-calendar/oauth/callback";
const URL_GOOGLE_CLOUD = "https://console.cloud.google.com/";
const URL_GCAL_API_LIBRARY =
  "https://console.cloud.google.com/apis/library/calendar-json.googleapis.com";
const URL_GCLOUD_CREDENTIALS =
  "https://console.cloud.google.com/apis/credentials";
const URL_GOOGLE_REDIRECT_URI_DOC =
  "https://developers.google.com/identity/protocols/oauth2/web-server#uri-validation";
const URL_GOOGLE_ACCOUNT_PERMISSIONS = "https://myaccount.google.com/permissions";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  btnLeft: {
    display: "flex",
    marginRight: "auto",
    marginLeft: 12,
  },
  colorAdorment: {
    width: 20,
    height: 20,
  },
  dialogTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
  },
  dialogTitleText: {
    flex: 1,
    fontWeight: 600,
  },
  gcalHelpTooltip: {
    maxWidth: 420,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.shadows[12],
    padding: theme.spacing(2),
    fontSize: "0.875rem",
    lineHeight: 1.55,
    opacity: 1,
  },
  gcalHelpTitle: {
    fontWeight: 700,
    marginBottom: theme.spacing(1.25),
    fontSize: "0.95rem",
    color: theme.palette.primary.main,
  },
  gcalHelpList: {
    margin: 0,
    paddingLeft: theme.spacing(2.25),
    "& li": {
      marginBottom: theme.spacing(1),
    },
  },
  gcalHelpIconBtn: {
    padding: theme.spacing(0.75),
  },
  gcalHelpLi: {
    fontSize: "0.8125rem",
    lineHeight: 1.55,
    marginBottom: theme.spacing(0.25),
  },
  gcalHelpLink: {
    fontWeight: 600,
    verticalAlign: "baseline",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  gcalHelpCodeBlock: {
    display: "block",
    marginTop: theme.spacing(0.75),
    marginBottom: theme.spacing(0.25),
    padding: theme.spacing(0.75, 1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontFamily: "Roboto Mono, Consolas, monospace",
    fontSize: "0.72rem",
    wordBreak: "break-all",
  },
  gcalHelpInlineCode: {
    fontFamily: "Roboto Mono, Consolas, monospace",
    fontSize: "0.78em",
    padding: "1px 4px",
    borderRadius: 3,
    backgroundColor: theme.palette.grey[200],
  },
  gcalRedirectCopyRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.75),
    maxWidth: "100%",
  },
  gcalRedirectCopyCode: {
    flex: 1,
    minWidth: 0,
    display: "block",
    marginTop: 0,
    marginBottom: 0,
    padding: theme.spacing(0.75, 1),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontFamily: "Roboto Mono, Consolas, monospace",
    fontSize: "0.72rem",
    wordBreak: "break-all",
  },
  gcalRedirectCopyBtn: {
    flexShrink: 0,
    marginTop: 2,
  },
  dialogActionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(1, 2),
  },
  dialogActionsLeft: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0,
  },
  dialogActionsRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  gcalConnectedBadge: {
    color: theme.palette.success.dark,
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.35,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "rgba(76, 175, 80, 0.12)",
    border: `1px solid ${theme.palette.success.light}`,
  },
}));

const DialogflowSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  // projectName: Yup.string()
  //   .min(3, "Too Short!")
  //   .max(100, "Too Long!")
  //   .required(),
  // jsonContent: Yup.string().min(3, "Too Short!").required(),
  // language: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required(),
});

const QueueIntegration = ({
  open,
  onClose,
  integrationId,
  integrationRecordSource = "queue",
}) => {
  const classes = useStyles();

  const initialState = {
    type: "typebot",
    name: "",
    projectName: "",
    jsonContent: "",
    language: "",
    urlN8N: "",
    typebotDelayMessage: 1000,
    typebotExpires: 1,
    typebotKeywordFinish: "",
    typebotKeywordRestart: "",
    typebotRestartMessage: "",
    typebotSlug: "",
    typebotUnknownMessage: "",
    urlixc: "",
    tokenixc: "",
    ipixc: "",
    clientId: "",
    clientSecret: "",
    calendarId: "primary",
    calendarIds: ["primary"],
    timeZone: "America/Sao_Paulo",
    connected: false,
  };

  const [integration, setIntegration] = useState(initialState);
  const [showIxcToken, setShowIxcToken] = useState(false);
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const [gcalCalendars, setGcalCalendars] = useState([]);
  const [gcalCalendarsLoading, setGcalCalendarsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      if (!integrationId) return;
      try {
        if (integrationRecordSource === "googleCalendar") {
          const { data } = await api.get(
            `/google-calendar-integrations/${integrationId}`
          );
          setIntegration((prev) => ({
            ...initialState,
            ...data,
            type: "google_calendar",
            calendarIds: Array.isArray(data.calendarIds)
              ? data.calendarIds
              : [data.calendarId || "primary"],
            clientSecret:
              typeof data.clientSecret === "string" ? data.clientSecret : "",
          }));
          return;
        }
        const { data } = await api.get(`/queueIntegration/${integrationId}`);
        setIntegration((prevState) => {
          return { ...prevState, ...data, ipixc: data.ipixc || data.urlixc || "" };
        });
      } catch (err) {
        toastError(err);
      }
    })();

    return () => {
      setIntegration({ ...initialState });
    };
  }, [integrationId, integrationRecordSource, open]);

  const fetchGcalCalendars = useCallback(async () => {
    if (!integrationId) return;
    setGcalCalendarsLoading(true);
    try {
      const { data } = await api.get(
        `/google-calendar-integrations/${integrationId}/calendars`
      );
      setGcalCalendars(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
      setGcalCalendars([]);
    } finally {
      setGcalCalendarsLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (
      !open ||
      !integrationId ||
      !integration.connected ||
      integrationRecordSource !== "googleCalendar"
    ) {
      setGcalCalendars([]);
      return;
    }
    fetchGcalCalendars();
  }, [
    open,
    integrationId,
    integration.connected,
    integrationRecordSource,
    fetchGcalCalendars,
  ]);

  const handleClose = () => {
    onClose();
    setIntegration({ ...initialState });
    setShowIxcToken(false);
    setShowGoogleSecret(false);
    setGcalCalendars([]);
  };

  const handleTestSession = async (event, values) => {
    try {
      const { projectName, jsonContent, language } = values;

      await api.post(`/queueIntegration/testSession`, {
        projectName,
        jsonContent,
        language,
      });

      toast.success(i18n.t("queueIntegrationModal.messages.testSuccess"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveDialogflow = async (values) => {
    try {
      if (values.type === "google_calendar") {
        if (!values.name?.trim()) {
          toast.error(i18n.t("queueIntegrationModal.form.name"));
          return;
        }
        if (!values.clientId?.trim()) {
          toast.error(i18n.t("queueIntegrationModal.form.googleClientId"));
          return;
        }
        if (!integrationId && !values.clientSecret?.trim()) {
          toast.error(i18n.t("queueIntegrationModal.form.googleClientSecret"));
          return;
        }
        const writeCal = (values.calendarId || "primary").trim() || "primary";
        const readIds = Array.isArray(values.calendarIds)
          ? [
              ...new Set(
                values.calendarIds
                  .map((x) => String(x || "").trim())
                  .filter(Boolean)
              ),
            ]
          : [writeCal];
        if (!readIds.length) {
          toast.error(
            i18n.t("queueIntegrationModal.messages.googleCalendarSelectCalendars")
          );
          return;
        }
        const body = {
          name: values.name.trim(),
          clientId: values.clientId.trim(),
          calendarId: writeCal,
          calendarIds: readIds,
          timeZone: (values.timeZone || "America/Sao_Paulo").trim(),
        };
        if (values.clientSecret?.trim()) {
          body.clientSecret = values.clientSecret.trim();
        }
        if (integrationId) {
          await api.put(`/google-calendar-integrations/${integrationId}`, body);
          toast.success(i18n.t("queueIntegrationModal.messages.editSuccess"));
        } else {
          await api.post("/google-calendar-integrations", {
            ...body,
            clientSecret: values.clientSecret.trim(),
          });
          toast.success(i18n.t("queueIntegrationModal.messages.addSuccess"));
        }
        handleClose();
        return;
      }
      if (
        values.type === "n8n" ||
        values.type === "webhook" ||
        values.type === "typebot" ||
        values.type === "flowbuilder" ||
        values.type === "ixc"
      )
        values.projectName = values.name;
      if (values.type === "ixc") {
        values.urlixc = values.urlixc || values.ipixc;
      }
      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, values);
        toast.success(i18n.t("queueIntegrationModal.messages.editSuccess"));
      } else {
        await api.post("/queueIntegration", values);
        toast.success(i18n.t("queueIntegrationModal.messages.addSuccess"));
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          validationSchema={DialogflowSchema}
          onSubmit={(values, actions, event) => {
            setTimeout(() => {
              handleSaveDialogflow(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => {
            const showGoogleCalendarHelp =
              values.type === "google_calendar" ||
              integrationRecordSource === "googleCalendar";
            const backendBase = String(
              process.env.REACT_APP_BACKEND_URL || ""
            ).replace(/\/$/, "");
            const resolvedGoogleRedirectUri =
              (values.redirectUri && String(values.redirectUri).trim()) ||
              (backendBase ? `${backendBase}${GCAL_OAUTH_REDIRECT_SUFFIX}` : "");

            const copyGoogleRedirectUri = async (e) => {
              if (e?.preventDefault) e.preventDefault();
              if (e?.stopPropagation) e.stopPropagation();
              if (!resolvedGoogleRedirectUri) {
                toast.error(
                  i18n.t(
                    "queueIntegrationModal.googleCalendarHelp.copyRedirectUriEmpty"
                  )
                );
                return;
              }
              try {
                await navigator.clipboard.writeText(resolvedGoogleRedirectUri);
                toast.success(
                  i18n.t(
                    "queueIntegrationModal.googleCalendarHelp.copyRedirectUriSuccess"
                  )
                );
              } catch (err) {
                toastError(err);
                toast.error(
                  i18n.t(
                    "queueIntegrationModal.googleCalendarHelp.copyRedirectUriError"
                  )
                );
              }
            };

            const extLink = (href, labelKey) => (
              <Link
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.gcalHelpLink}
              >
                {i18n.t(`queueIntegrationModal.googleCalendarHelp.${labelKey}`)}
              </Link>
            );

            const gcalHelpTitle = (
              <div>
                <Typography
                  component="div"
                  variant="subtitle1"
                  className={classes.gcalHelpTitle}
                >
                  {i18n.t("queueIntegrationModal.googleCalendarHelp.title")}
                </Typography>
                <ol className={classes.gcalHelpList}>
                  <li>
                    <Typography
                      component="div"
                      variant="body2"
                      className={classes.gcalHelpLi}
                    >
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step1a")}{" "}
                      {extLink(URL_GOOGLE_CLOUD, "linkCloudConsole")}
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step1b")}{" "}
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step1c")}{" "}
                      {extLink(URL_GCAL_API_LIBRARY, "linkCalendarApi")}.{" "}
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step1d")}{" "}
                      {extLink(URL_GCLOUD_CREDENTIALS, "linkCredentials")}
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step1e")}{" "}
                      <strong>
                        {i18n.t("queueIntegrationModal.googleCalendarHelp.step1f")}
                      </strong>
                      .
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      component="div"
                      variant="body2"
                      className={classes.gcalHelpLi}
                    >
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step2a")}{" "}
                      <span className={classes.gcalHelpInlineCode}>
                        {GCAL_OAUTH_REDIRECT_SUFFIX}
                      </span>{" "}
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step2b")}
                      {resolvedGoogleRedirectUri ? (
                        <div className={classes.gcalRedirectCopyRow}>
                          <span className={classes.gcalRedirectCopyCode}>
                            {resolvedGoogleRedirectUri}
                          </span>
                          <Tooltip
                            title={i18n.t(
                              "queueIntegrationModal.googleCalendarHelp.copyRedirectUri"
                            )}
                          >
                            <IconButton
                              type="button"
                              size="small"
                              className={classes.gcalRedirectCopyBtn}
                              onClick={copyGoogleRedirectUri}
                              aria-label={i18n.t(
                                "queueIntegrationModal.googleCalendarHelp.copyRedirectUri"
                              )}
                            >
                              <FileCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                      ) : null}{" "}
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step2c")}{" "}
                      {extLink(
                        URL_GOOGLE_REDIRECT_URI_DOC,
                        "linkRedirectDoc"
                      )}
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      component="div"
                      variant="body2"
                      className={classes.gcalHelpLi}
                    >
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step3a")}{" "}
                      {extLink(
                        URL_GOOGLE_ACCOUNT_PERMISSIONS,
                        "linkAccountPermissions"
                      )}{" "}
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step3b")}
                    </Typography>
                  </li>
                  <li>
                    <Typography
                      component="div"
                      variant="body2"
                      className={classes.gcalHelpLi}
                    >
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step4a")}{" "}
                      <Link
                        component={RouterLink}
                        to="/flowbuilders"
                        className={classes.gcalHelpLink}
                      >
                        {i18n.t(
                          "queueIntegrationModal.googleCalendarHelp.linkFlowBuilder"
                        )}
                      </Link>
                      {i18n.t("queueIntegrationModal.googleCalendarHelp.step4b")}
                    </Typography>
                  </li>
                </ol>
              </div>
            );
            return (
            <Form>
              <DialogTitle
                disableTypography
                id="queue-integration-dialog-title"
                className={classes.dialogTitleRow}
              >
                <Typography
                  variant="h6"
                  component="span"
                  className={classes.dialogTitleText}
                >
                  {integrationId
                    ? `${i18n.t("queueIntegrationModal.title.edit")}`
                    : `${i18n.t("queueIntegrationModal.title.add")}`}
                </Typography>
                {showGoogleCalendarHelp && (
                  <Tooltip
                    interactive
                    enterDelay={250}
                    leaveDelay={250}
                    placement="bottom-end"
                    classes={{ tooltip: classes.gcalHelpTooltip }}
                    title={gcalHelpTitle}
                    PopperProps={{ style: { zIndex: 2000 } }}
                  >
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={i18n.t(
                        "queueIntegrationModal.googleCalendarHelp.aria"
                      )}
                      className={classes.gcalHelpIconBtn}
                    >
                      <HelpOutline />
                    </IconButton>
                  </Tooltip>
                )}
              </DialogTitle>
              <Paper square className={classes.mainPaper} elevation={1}>
                <DialogContent dividers>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={6} xl={6}>
                      <FormControl
                        variant="outlined"
                        className={classes.formControl}
                        margin="dense"
                        fullWidth
                      >
                        <InputLabel id="type-selection-input-label">
                          {i18n.t("queueIntegrationModal.form.type")}
                        </InputLabel>

                        <Field
                          as={Select}
                          label={i18n.t("queueIntegrationModal.form.type")}
                          name="type"
                          labelId="profile-selection-label"
                          error={touched.type && Boolean(errors.type)}
                          helpertext={touched.type && errors.type}
                          id="type"
                          required
                          disabled={
                            Boolean(integrationId) &&
                            integrationRecordSource === "googleCalendar"
                          }
                        >
                          <MenuItem value="dialogflow">DialogFlow</MenuItem>
                          <MenuItem value="n8n">N8N</MenuItem>
                          <MenuItem value="webhook">WebHooks</MenuItem>
                          <MenuItem value="typebot">Typebot</MenuItem>
                          <MenuItem value="flowbuilder">Flowbuilder</MenuItem>
                          <MenuItem value="ixc">IXC Soft</MenuItem>
                          <MenuItem value="google_calendar">
                            {i18n.t("queueIntegrationModal.form.googleCalendar")}
                          </MenuItem>
                        </Field>
                      </FormControl>
                    </Grid>
                    {values.type === "dialogflow" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            fullWidth
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <FormControl
                            variant="outlined"
                            className={classes.formControl}
                            margin="dense"
                            fullWidth
                          >
                            <InputLabel id="language-selection-input-label">
                              {i18n.t("queueIntegrationModal.form.language")}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t(
                                "queueIntegrationModal.form.language"
                              )}
                              name="language"
                              labelId="profile-selection-label"
                              fullWidth
                              error={
                                touched.language && Boolean(errors.language)
                              }
                              helpertext={touched.language && errors.language}
                              id="language-selection"
                              required
                            >
                              <MenuItem value="pt-BR">Portugues</MenuItem>
                              <MenuItem value="en">Inglês</MenuItem>
                              <MenuItem value="es">Español</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.projectName"
                            )}
                            name="projectName"
                            error={
                              touched.projectName && Boolean(errors.projectName)
                            }
                            helpertext={
                              touched.projectName && errors.projectName
                            }
                            fullWidth
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.jsonContent"
                            )}
                            type="jsonContent"
                            multiline
                            //inputRef={greetingRef}
                            maxRows={5}
                            minRows={5}
                            fullWidth
                            name="jsonContent"
                            error={
                              touched.jsonContent && Boolean(errors.jsonContent)
                            }
                            helpertext={
                              touched.jsonContent && errors.jsonContent
                            }
                            variant="outlined"
                            margin="dense"
                          />
                        </Grid>
                      </>
                    )}

                    {(values.type === "n8n" || values.type === "webhook") && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            required
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helpertext={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                      </>
                    )}

                    {values.type === "flowbuilder" && (
                      <Grid item xs={12} md={6} xl={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("queueIntegrationModal.form.name")}
                          autoFocus
                          name="name"
                          fullWidth
                          error={touched.name && Boolean(errors.name)}
                          helpertext={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                        />
                      </Grid>
                    )}

                    {values.type === "typebot" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.urlN8N")}
                            name="urlN8N"
                            error={touched.urlN8N && Boolean(errors.urlN8N)}
                            helpertext={touched.urlN8N && errors.urlN8N}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotSlug"
                            )}
                            name="typebotSlug"
                            error={
                              touched.typebotSlug && Boolean(errors.typebotSlug)
                            }
                            helpertext={
                              touched.typebotSlug && errors.typebotSlug
                            }
                            required
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotExpires"
                            )}
                            name="typebotExpires"
                            error={
                              touched.typebotExpires &&
                              Boolean(errors.typebotExpires)
                            }
                            helpertext={
                              touched.typebotExpires && errors.typebotExpires
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotDelayMessage"
                            )}
                            name="typebotDelayMessage"
                            error={
                              touched.typebotDelayMessage &&
                              Boolean(errors.typebotDelayMessage)
                            }
                            helpertext={
                              touched.typebotDelayMessage &&
                              errors.typebotDelayMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotKeywordFinish"
                            )}
                            name="typebotKeywordFinish"
                            error={
                              touched.typebotKeywordFinish &&
                              Boolean(errors.typebotKeywordFinish)
                            }
                            helpertext={
                              touched.typebotKeywordFinish &&
                              errors.typebotKeywordFinish
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotKeywordRestart"
                            )}
                            name="typebotKeywordRestart"
                            error={
                              touched.typebotKeywordRestart &&
                              Boolean(errors.typebotKeywordRestart)
                            }
                            helpertext={
                              touched.typebotKeywordRestart &&
                              errors.typebotKeywordRestart
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotUnknownMessage"
                            )}
                            name="typebotUnknownMessage"
                            error={
                              touched.typebotUnknownMessage &&
                              Boolean(errors.typebotUnknownMessage)
                            }
                            helpertext={
                              touched.typebotUnknownMessage &&
                              errors.typebotUnknownMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.typebotRestartMessage"
                            )}
                            name="typebotRestartMessage"
                            error={
                              touched.typebotRestartMessage &&
                              Boolean(errors.typebotRestartMessage)
                            }
                            helpertext={
                              touched.typebotRestartMessage &&
                              errors.typebotRestartMessage
                            }
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                      </>
                    )}

                    {values.type === "google_calendar" && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            {i18n.t("queueIntegrationModal.form.googleOAuthHint")}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t("queueIntegrationModal.form.name")}
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.googleClientId"
                            )}
                            name="clientId"
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.googleClientSecret"
                            )}
                            name="clientSecret"
                            type={showGoogleSecret ? "text" : "password"}
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                            helperText={
                              integrationId
                                ? i18n.t(
                                    "queueIntegrationModal.form.googleClientSecretHintEdit"
                                  )
                                : i18n.t(
                                    "queueIntegrationModal.form.googleClientSecretHintNew"
                                  )
                            }
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={
                                      showGoogleSecret
                                        ? "Ocultar secret"
                                        : "Mostrar secret"
                                    }
                                    onClick={() =>
                                      setShowGoogleSecret(!showGoogleSecret)
                                    }
                                    edge="end"
                                  >
                                    {showGoogleSecret ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        {values.connected &&
                        integrationId &&
                        integrationRecordSource === "googleCalendar" ? (
                          <>
                            <Grid item xs={12}>
                              <Box
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 8,
                                }}
                              >
                                <Button
                                  type="button"
                                  variant="outlined"
                                  size="small"
                                  disabled={gcalCalendarsLoading}
                                  onClick={() => fetchGcalCalendars()}
                                >
                                  {i18n.t(
                                    "queueIntegrationModal.form.googleCalendarRefreshList"
                                  )}
                                </Button>
                                {gcalCalendarsLoading ? (
                                  <CircularProgress size={22} />
                                ) : null}
                              </Box>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                component="div"
                                style={{ marginTop: 8 }}
                              >
                                {i18n.t(
                                  "queueIntegrationModal.form.googleCalendarListHint"
                                )}
                              </Typography>
                            </Grid>
                            {gcalCalendars.length ? (
                              <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                  {i18n.t(
                                    "queueIntegrationModal.form.googleCalendarReadCalendars"
                                  )}
                                </Typography>
                                <Paper variant="outlined" style={{ padding: 8 }}>
                                  {(() => {
                                    const listedIds = new Set(
                                      gcalCalendars.map((c) => c.id)
                                    );
                                    const orphanIds = (
                                      values.calendarIds || []
                                    ).filter((id) => !listedIds.has(id));
                                    const toggleId = (calId, checked) => {
                                      const cur = values.calendarIds || [];
                                      if (checked) {
                                        setFieldValue("calendarIds", [
                                          ...new Set([...cur, calId]),
                                        ]);
                                        return;
                                      }
                                      const next = cur.filter((x) => x !== calId);
                                      if (!next.length) {
                                        toast.warning(
                                          i18n.t(
                                            "queueIntegrationModal.messages.googleCalendarSelectCalendars"
                                          )
                                        );
                                        return;
                                      }
                                      setFieldValue("calendarIds", next);
                                      if (values.calendarId === calId) {
                                        setFieldValue(
                                          "calendarId",
                                          next[0] || "primary"
                                        );
                                      }
                                    };
                                    return (
                                      <>
                                        {gcalCalendars.map((cal) => (
                                          <FormControlLabel
                                            key={cal.id}
                                            control={
                                              <Checkbox
                                                color="primary"
                                                checked={(
                                                  values.calendarIds || []
                                                ).includes(cal.id)}
                                                onChange={(e) =>
                                                  toggleId(cal.id, e.target.checked)
                                                }
                                              />
                                            }
                                            label={`${cal.summary || cal.id}${
                                              cal.primary
                                                ? ` (${i18n.t(
                                                    "queueIntegrationModal.form.googleCalendarPrimaryBadge"
                                                  )})`
                                                : ""
                                            }`}
                                          />
                                        ))}
                                        {orphanIds.map((oid) => (
                                          <FormControlLabel
                                            key={`orphan-${oid}`}
                                            control={
                                              <Checkbox
                                                color="primary"
                                                checked
                                                onChange={(e) =>
                                                  toggleId(oid, e.target.checked)
                                                }
                                              />
                                            }
                                            label={`${oid} (${i18n.t(
                                              "queueIntegrationModal.form.googleCalendarOrphanBadge"
                                            )})`}
                                          />
                                        ))}
                                      </>
                                    );
                                  })()}
                                </Paper>
                              </Grid>
                            ) : !gcalCalendarsLoading ? (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">
                                  {i18n.t(
                                    "queueIntegrationModal.form.googleCalendarEmptyList"
                                  )}
                                </Typography>
                              </Grid>
                            ) : null}
                            <Grid item xs={12} md={6} xl={6}>
                              <FormControl
                                variant="outlined"
                                margin="dense"
                                fullWidth
                                className={classes.textField}
                              >
                                <InputLabel id="gcal-write-cal-label">
                                  {i18n.t(
                                    "queueIntegrationModal.form.googleCalendarWriteCalendar"
                                  )}
                                </InputLabel>
                                <Select
                                  labelId="gcal-write-cal-label"
                                  label={i18n.t(
                                    "queueIntegrationModal.form.googleCalendarWriteCalendar"
                                  )}
                                  value={values.calendarId || "primary"}
                                  onChange={(e) =>
                                    setFieldValue("calendarId", e.target.value)
                                  }
                                >
                                  {(() => {
                                    const ids = new Set(
                                      (gcalCalendars || []).map((c) => c.id)
                                    );
                                    const cur = values.calendarId || "primary";
                                    const extra =
                                      cur && !ids.has(cur)
                                        ? [
                                            {
                                              id: cur,
                                              summary: `${cur} (${i18n.t(
                                                "queueIntegrationModal.form.googleCalendarNotInListSuffix"
                                              )})`,
                                            },
                                          ]
                                        : [];
                                    const opts = (gcalCalendars || []).length
                                      ? [...extra, ...gcalCalendars]
                                      : [
                                          {
                                            id: cur,
                                            summary: cur,
                                          },
                                        ];
                                    return opts.map((cal) => (
                                      <MenuItem key={cal.id} value={cal.id}>
                                        {cal.summary || cal.id}
                                      </MenuItem>
                                    ));
                                  })()}
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={12} md={6} xl={6}>
                            <Field
                              as={TextField}
                              label={i18n.t(
                                "queueIntegrationModal.form.googleCalendarId"
                              )}
                              name="calendarId"
                              variant="outlined"
                              margin="dense"
                              fullWidth
                              className={classes.textField}
                              helperText={i18n.t(
                                "queueIntegrationModal.form.googleCalendarIdManualHint"
                              )}
                            />
                          </Grid>
                        )}
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label={i18n.t(
                              "queueIntegrationModal.form.googleTimeZone"
                            )}
                            name="timeZone"
                            variant="outlined"
                            margin="dense"
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        {resolvedGoogleRedirectUri ? (
                          <Grid item xs={12}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              component="div"
                              style={{ marginBottom: 4 }}
                            >
                              Redirect URI (Google Cloud):
                            </Typography>
                            <div className={classes.gcalRedirectCopyRow}>
                              <span className={classes.gcalRedirectCopyCode}>
                                {resolvedGoogleRedirectUri}
                              </span>
                              <Tooltip
                                title={i18n.t(
                                  "queueIntegrationModal.googleCalendarHelp.copyRedirectUri"
                                )}
                              >
                                <IconButton
                                  type="button"
                                  size="small"
                                  className={classes.gcalRedirectCopyBtn}
                                  onClick={copyGoogleRedirectUri}
                                  aria-label={i18n.t(
                                    "queueIntegrationModal.googleCalendarHelp.copyRedirectUri"
                                  )}
                                >
                                  <FileCopy fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </Grid>
                        ) : null}
                      </>
                    )}

                    {values.type === "ixc" && (
                      <>
                        <Grid item xs={12} md={6} xl={6}>
                          <Field
                            as={TextField}
                            label="Nome da Integração"
                            autoFocus
                            name="name"
                            error={touched.name && Boolean(errors.name)}
                            helpertext={touched.name && errors.name}
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label="URL do Servidor IXC"
                            name="ipixc"
                            error={touched.ipixc && Boolean(errors.ipixc)}
                            helpertext={
                              touched.ipixc && errors.ipixc
                                ? errors.ipixc
                                : "Apenas a URL base (ex: https://sistema.opalink.com.br), sem /webservice/ ou outros caminhos."
                            }
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            placeholder="https://sistema.opalink.com.br"
                            className={classes.textField}
                          />
                        </Grid>
                        <Grid item xs={12} md={12} xl={12}>
                          <Field
                            as={TextField}
                            label="Token/Chave API IXC"
                            name="tokenixc"
                            type={showIxcToken ? "text" : "password"}
                            error={
                              touched.tokenixc && Boolean(errors.tokenixc)
                            }
                            helpertext={
                              touched.tokenixc && errors.tokenixc
                                ? errors.tokenixc
                                : "Formato: usuário:senha do login do IXC Soft (ex: admin:minhasenha). Será enviado em Base64."
                            }
                            variant="outlined"
                            margin="dense"
                            required
                            fullWidth
                            placeholder="usuario:senha"
                            className={classes.textField}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={showIxcToken ? "Ocultar token" : "Mostrar token"}
                                    onClick={() => setShowIxcToken(!showIxcToken)}
                                    edge="end"
                                  >
                                    {showIxcToken ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={async () => {
                              try {
                                const resp = await api.post(
                                  "/ixc/testar-conexao",
                                  {
                                    urlixc: values.ipixc,
                                    tokenixc: values.tokenixc,
                                  }
                                );
                                if (resp.data.success) {
                                  toast.success(resp.data.message);
                                } else {
                                  toast.error(resp.data.message);
                                }
                              } catch (err) {
                                toastError(err);
                              }
                            }}
                            disabled={!values.ipixc || !values.tokenixc}
                          >
                            Testar Conexão
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </DialogContent>
              </Paper>

              <DialogActions className={classes.dialogActionsBar}>
                <Box className={classes.dialogActionsLeft}>
                  {values.type === "google_calendar" && integrationId && (
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={isSubmitting}
                      onClick={async () => {
                        try {
                          const { data } = await api.get(
                            `/google-calendar-integrations/${integrationId}/oauth-url`,
                            { params: { returnTo: "queue-integration" } }
                          );
                          if (data?.url) {
                            window.location.assign(data.url);
                          }
                        } catch (err) {
                          toastError(err);
                        }
                      }}
                    >
                      {i18n.t("queueIntegrationModal.buttons.connectGoogle")}
                    </Button>
                  )}
                  {values.type === "dialogflow" && (
                    <Button
                      onClick={(e) => handleTestSession(e, values)}
                      color="inherit"
                      disabled={isSubmitting}
                      name="testSession"
                      variant="outlined"
                      className={classes.btnLeft}
                    >
                      {i18n.t("queueIntegrationModal.buttons.test")}
                    </Button>
                  )}
                  {values.type === "google_calendar" &&
                    integrationId &&
                    Boolean(values.connected) && (
                      <Typography
                        component="span"
                        variant="body2"
                        className={classes.gcalConnectedBadge}
                      >
                        {i18n.t(
                          "queueIntegrationModal.buttons.googleCalendarConnectedBadge"
                        )}
                        {values.updatedAt
                          ? ` · ${i18n.t(
                              "queueIntegrationModal.buttons.googleCalendarConnectedAt"
                            )} ${new Date(values.updatedAt).toLocaleString(
                              i18n.language?.startsWith("en") ? "en-US" : "pt-BR",
                              {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}`
                          : ""}
                      </Typography>
                    )}
                </Box>
                <Box className={classes.dialogActionsRight}>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isSubmitting}
                    variant="outlined"
                  >
                    {i18n.t("queueIntegrationModal.buttons.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {integrationId
                      ? `${i18n.t("queueIntegrationModal.buttons.okEdit")}`
                      : `${i18n.t("queueIntegrationModal.buttons.okAdd")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                </Box>
              </DialogActions>
            </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QueueIntegration;
